<template>
  <p
    v-if="failed || missingVideoSource"
    role="status"
    data-cy="ds-light-box__error"
  >
    {{ t("design_system.light_box.media_error") }}
  </p>
  <template v-else>
    <img
      v-if="item.type === DS_LIGHT_BOX_MEDIA_TYPE.Image"
      :class="classes.Media"
      :src="item.src"
      :alt="item.alt ?? item.name"
      draggable="false"
      data-cy="ds-light-box__image"
      @error="onImageError"
    />
    <template v-else>
      <div
        v-if="item.type === DS_LIGHT_BOX_MEDIA_TYPE.Audio"
        :class="classes.Artwork"
        aria-hidden="true"
      >
        <AudioArtwork
          :class="classes.ArtworkImage"
          viewBox="0 0 16 16"
          focusable="false"
        />
      </div>
      <component
        :is="isVideo ? DS_LIGHT_BOX_MEDIA_TYPE.Video : DS_LIGHT_BOX_MEDIA_TYPE.Audio"
        v-if="!deferredVideo"
        ref="player"
        :class="isVideo ? classes.Media : classes.Audio"
        :src="item.src"
        :poster="isVideo && !thumbnailFailed ? thumbnailSrc : undefined"
        :aria-label="item.name"
        tabindex="0"
        controls
        preload="metadata"
        playsinline
        data-cy="ds-light-box__player"
        @play="onPlay"
        @pause="onPause"
        @ended="onEnded"
        @error="onError"
      />
      <img
        v-if="deferredVideo && !thumbnailFailed"
        :class="classes.Media"
        :src="thumbnailSrc"
        :alt="item.alt ?? item.name"
        draggable="false"
        data-cy="ds-light-box__video-thumbnail"
        @error="onThumbnailError"
      />
      <DSButton
        v-if="!playing"
        ref="playButton"
        :class="classes.Play"
        :aria-label="t('design_system.light_box.play')"
        data-cy="ds-light-box__play"
        @click="onPlayClick"
        ><DSIcon
          icon="play"
          size="xl"
          aria-hidden="true"
      /></DSButton>
    </template>
  </template>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import AudioArtwork from "@public/icons/outlined/file-audio.svg?component";
import { DS_LIGHT_BOX_CLASSES as classes } from "../constants/classes";
import { DS_LIGHT_BOX_MEDIA_TYPE, type DSLightBoxItem } from "../types/props";

const props = defineProps<{ item: DSLightBoxItem }>();
const t = window.EasyLocale.getLocale;
const player = useTemplateRef<HTMLMediaElement>("player");
const playButton = useTemplateRef<InstanceType<typeof DSButton>>("playButton");
const failed = ref(false);
const playing = ref(false);
const requested = ref(false);
const thumbnailFailed = ref(false);
const isVideo = computed(() => props.item.type === DS_LIGHT_BOX_MEDIA_TYPE.Video);
const thumbnailSrc = computed(() => props.item.thumbnailSrc?.trim());
const missingVideoSource = computed(() => isVideo.value && !props.item.src?.trim());
const deferredVideo = computed(() => isVideo.value && Boolean(thumbnailSrc.value) && !requested.value);
let disposed = false;

const onThumbnailError = () => {
  thumbnailFailed.value = true;
};

const onImageError = () => {
  failed.value = true;
};
const onPause = () => {
  playing.value = false;
};
const onEnded = () => {
  playing.value = false;
};

const onPlay = () => {
  if (playButton.value?.$el === document.activeElement) player.value?.focus();
  playing.value = true;
};

const stop = () => player.value?.pause();
const onError = () => {
  stop();
  failed.value = true;
};
const handlePlayRejection = (error: unknown) => {
  if (disposed) return;
  if (error instanceof DOMException && ["AbortError", "NotAllowedError"].includes(error.name)) return;
  onError();
};
const onPlayClick = async () => {
  requested.value = true;
  await nextTick();
  const media = player.value;
  if (!media || disposed) return;
  try {
    await media.play();
    if (disposed) media.pause();
  } catch (error) {
    handlePlayRejection(error);
  }
};
onBeforeUnmount(() => {
  disposed = true;
  stop();
});
</script>
